import type { OrganizationsListFilters } from '@openx/components-organization/lib/types';
import { useOrganizationContext } from '@openx/components-organization/lib/utils/context/OrganizationContext';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing';
import type { OrganizationsList, OrganizationTreeRowData } from '@openx/types/organization';
import type { CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import { OPEN_AUDIENCE_ORG_EXTERNAL_ID } from '@openx/utils/services/config';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo } from 'react';
import { useHandleListCriteriaOnCompleted } from '../../../../hooks/useHandleListCriteriaOnCompleted';
import { useListCriteria } from '../../../../utils/useListCriteria';
import { defaultCriteria } from '../../constants';

export const useGetOrganizationsDataAndCriteria = () => {
  const { useOrganizationsListFetch } = useOrganizationContext();
  const { criteria, setCriteria } = useListCriteria('organizations', defaultCriteria);

  const queryCrit = useMemo(
    () =>
      serverSideCriteriaProcessing(
        {
          ...criteria,
          filters: {
            byPhrase: {
              fields: ['name'],
              phrase: criteria.filters?.phrase ?? '',
            },
          },
        },
        [defaultCriteria.sort]
      ),
    [criteria]
  );

  const handleCompleted = useHandleListCriteriaOnCompleted<OrganizationsListFilters>(setCriteria);

  // adjusting initial queryCrit to ensure that we always fetch organizations under the root organization (OPEN_AUDIENCE_ORG_EXTERNAL_ID)
  // because we're displaying a tree view of organizations on the list
  const adjustQueryCrit = useMemo(
    () => ({
      ...queryCrit,
      where: queryCrit.where ?? { parent_account_id: { _eq: OPEN_AUDIENCE_ORG_EXTERNAL_ID } },
    }),
    [queryCrit]
  );

  const { error, loading, organizations } = useOrganizationsListFetch({
    onCompleted: handleCompleted,
    variables: adjustQueryCrit,
  });

  const handleCriteriaChange = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, OrganizationsListFilters>) => {
      setCriteria(prevCriteria => {
        const newCriteriaProcessed = applyCriteriaChange(prevCriteria, newCriteria);
        if (!isEqual(newCriteriaProcessed, prevCriteria)) {
          return newCriteriaProcessed;
        }
        return prevCriteria;
      });
    },
    [setCriteria]
  );

  const organizationsTreeViewData: OrganizationTreeRowData[] = useMemo(() => {
    const mapToTreeRow = (org: OrganizationsList, depth = 0): OrganizationTreeRowData => ({
      ...org,
      depth,
      expanded: false,
      children: org.sub_accounts?.map(child => mapToTreeRow(child, depth + 1)) || [],
    });

    return organizations.map(org => mapToTreeRow(org));
  }, [organizations]);

  return {
    error,
    loading,
    organizations: organizationsTreeViewData,
    criteria,
    handleCriteriaChange,
  };
};
